import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { HERO_SLIDES, CONFERENCE_TITLE, HERO_PEOPLE_PHOTOS } from '@/data/homeContent.js';
import { useConferenceDates } from '@/context/ConferenceConfigContext.jsx';
import { cn } from '@/lib/cn.js';
import { OptimizedImage } from '@components/ui/OptimizedImage.jsx';
import { HeroPeopleMosaic } from '@components/home/HeroPeopleMosaic.jsx';
import { usePreloadImages } from '@/hooks/usePreloadImages.js';

const HERO_IMAGE_URLS = [
  ...HERO_SLIDES.flatMap((s) => [s.image, s.foreground].filter(Boolean)),
  ...HERO_PEOPLE_PHOTOS.map((p) => p.src),
];

const HERO_IMAGE_FILTER = 'brightness-[1.12] contrast-[1.06] saturate-[1.15]';
const DWELL_MS = 2600;       // fallback dwell when a slide omits its own timing
const DWELL_WORD_MS = 2600;  // Wat Arun slide (allows for the word intro)
const INTRO_MS = 1500;       // how long the big word holds before content

/* ── "ISBM 2026" — staggered per-letter reveal, then fully fades away ── */
const WORD_CONTAINER = {
  hidden: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
  show:   { transition: { delayChildren: 0.12, staggerChildren: 0.06 } },
  gone:   { transition: { staggerChildren: 0.03 } },
};

const WORD_LETTER = {
  hidden: { opacity: 0, y: '110%', scale: 0.5, filter: 'blur(14px)' },
  show: {
    opacity: 1, y: '0%', scale: 1, filter: 'blur(0px)',
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
  // Disappears completely once revealed — no lingering watermark.
  gone: {
    opacity: 0, y: '-32%', scale: 1.1, filter: 'blur(8px)',
    transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1] },
  },
};

/* First slide that declares an animatedWord */
const WORD_SLIDE = HERO_SLIDES.find((s) => s.animatedWord);

export function HeroSlider() {
  const [index, setIndex]       = useState(0);
  const [intro, setIntro]       = useState(Boolean(HERO_SLIDES[0].foreground));
  const [progress, setProgress] = useState(0);
  const startRef = useRef(Date.now());
  const rafRef   = useRef(null);
  const reduce   = useReducedMotion();
  const { cityCountry, displayRange } = useConferenceDates();

  usePreloadImages(HERO_IMAGE_URLS, { urgent: true });

  const isWatArun = Boolean(HERO_SLIDES[index].foreground);
  // Each slide may define its own dwell; fall back to the shared defaults.
  const dwell = HERO_SLIDES[index].dwell ?? (isWatArun ? DWELL_WORD_MS : DWELL_MS);

  /* ── Auto-advance + progress bar. Re-armed on every index change so manual
        navigation restarts the full dwell instead of jumping immediately. ── */
  useEffect(() => {
    if (reduce) return undefined;
    startRef.current = Date.now();
    setProgress(0);

    const tick = () => {
      const p = Math.min((Date.now() - startRef.current) / dwell, 1);
      setProgress(p);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    const id = setTimeout(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), dwell);

    return () => {
      clearTimeout(id);
      cancelAnimationFrame(rafRef.current);
    };
  }, [index, reduce, dwell]);

  /* ── Wat Arun intro: hold the big word, then reveal content after 1.5s. ── */
  useEffect(() => {
    if (HERO_SLIDES[index].foreground && !reduce) {
      setIntro(true);
      const t = setTimeout(() => setIntro(false), INTRO_MS);
      return () => clearTimeout(t);
    }
    setIntro(false);
    return undefined;
  }, [index, reduce]);

  const goTo = useCallback((i) => {
    setIndex(i);
    setProgress(0);
  }, []);
  const prev = useCallback(
    () => goTo((index - 1 + HERO_SLIDES.length) % HERO_SLIDES.length),
    [index, goTo],
  );
  const next = useCallback(
    () => goTo((index + 1) % HERO_SLIDES.length),
    [index, goTo],
  );

  const slide = HERO_SLIDES[index];
  const isPeopleSlide = Boolean(slide.peopleMosaic);

  /* Word variant: bold reveal during the intro, then fully gone. Hidden on the
     other slides. The word layer never unmounts. */
  const wordVariant =
    !WORD_SLIDE || reduce
      ? 'gone'
      : !isWatArun
        ? 'hidden'
        : intro
          ? 'show'
          : 'gone';

  return (
    <section className="relative overflow-hidden min-h-[92vh] flex items-center">
      {/* ── Background images — all mounted, pure opacity cross-fade ── */}
      <div className="absolute inset-0 -z-30">
        {HERO_SLIDES.map((s, i) => (
          <motion.div
            key={s.id ?? s.image}
            initial={false}
            animate={{ opacity: i === index ? 1 : 0 }}
            transition={{ duration: 1.1, ease: 'easeInOut' }}
            className="absolute inset-0"
            aria-hidden={i !== index}
          >
            {s.peopleMosaic ? (
              <HeroPeopleMosaic />
            ) : (
              <OptimizedImage
                src={s.image}
                alt=""
                className={cn('h-full w-full object-cover object-center', HERO_IMAGE_FILTER)}
                priority={i === 0}
              />
            )}
          </motion.div>
        ))}
      </div>

      {/* ── Overlays (skipped on people mosaic — photos stay bright) ── */}
      {!isPeopleSlide && (
        <>
          <div aria-hidden className="absolute inset-0 -z-20 bg-bangkok-hero" />
          <div aria-hidden className="absolute inset-0 -z-20 bg-gradient-to-r from-ink-950/55 via-ink-950/30 to-ink-950/10" />
          <div aria-hidden className="absolute inset-0 -z-20 opacity-20 bg-bangkok-sunset mix-blend-multiply" />
          <div aria-hidden className="absolute inset-0 -z-20 opacity-[0.15] bg-thai-pattern" />
        </>
      )}

      {/* ── "ISBM 2026" — centered reveal then watermark. Always mounted. ── */}
      {WORD_SLIDE && (
        <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center px-6">
          <div className="relative flex items-center justify-center">
            {/* Warm radial glow, visible only during the intro */}
            <motion.span
              aria-hidden
              initial={false}
              animate={{ opacity: isWatArun && !reduce && intro ? 1 : 0 }}
              transition={{ duration: 1.1, ease: 'easeOut' }}
              className="pointer-events-none absolute left-1/2 top-1/2 h-[160%] w-[90%]
                         -translate-x-1/2 -translate-y-1/2 rounded-full blur-2xl
                         bg-[radial-gradient(closest-side,rgba(255,170,60,0.5),rgba(255,120,30,0.16),transparent)]"
            />
            <motion.span
              aria-label={WORD_SLIDE.animatedWord}
              role="text"
              initial="hidden"
              animate={wordVariant}
              variants={WORD_CONTAINER}
              className="relative inline-flex font-display font-black uppercase leading-none
                         text-center tracking-[0.08em] sm:tracking-[0.14em]"
              style={{ fontSize: 'clamp(2.75rem, 13vw, 12rem)' }}
            >
              {[...WORD_SLIDE.animatedWord].map((ch, ci) =>
                ch === ' ' ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={`sp-${ci}`} aria-hidden className="inline-block w-[0.3em]" />
                ) : (
                  <motion.span
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${ch}-${ci}`}
                    variants={WORD_LETTER}
                    className="inline-block bg-gradient-to-b from-white via-amber-100 to-amber-300
                               bg-clip-text text-transparent drop-shadow-[0_6px_34px_rgba(255,150,40,0.5)]"
                  >
                    {ch}
                  </motion.span>
                ),
              )}
            </motion.span>
          </div>
        </div>
      )}

      {/* ── Slide content — keyed per slide, animates in/out on every change.
           popLayout keeps the exiting copy out of flow so nothing jumps. ── */}
      <div className="container relative z-10 w-full pt-24 pb-20 lg:pt-32 lg:pb-28">
        {/* `initial` is enabled so the first slide's content respects the word-intro
            delay below; without it the copy would pop in while "ISBM 2026" animates. */}
        <AnimatePresence mode="popLayout">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
              opacity: 0,
              y: -18,
              transition: { duration: 0.4, ease: [0.4, 0, 1, 1] },
            }}
            transition={{
              duration: 0.7,
              ease: [0.22, 1, 0.36, 1],
              /* Wait for the word intro on Wat Arun; lead with the cross-fade elsewhere. */
              delay: isWatArun && !reduce ? INTRO_MS / 1000 + 0.1 : 0.2,
            }}
            className="max-w-5xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]"
          >
            <p className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl text-accent-400 font-semibold mb-5">
              <span className="inline-flex items-start gap-1 tracking-normal">
                <span className="inline-flex items-start tabular-nums leading-none">
                  <span>{CONFERENCE_TITLE.editionNumber}</span>
                  <sup className="normal-case text-[0.45em] font-semibold leading-none mt-[0.12em] -ml-[0.04em]">
                    {CONFERENCE_TITLE.editionOrdinal}
                  </sup>
                </span>
                <span className="leading-none pt-[0.08em]">Edition</span>
              </span>
            </p>

            <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-balance leading-[1.05] text-white">
              <span className="block font-bold">{slide.title}</span>
              <span className="block font-normal mt-2 text-white/95">{slide.subtitle}</span>
            </h1>

            <p className="mt-6 text-xl sm:text-2xl font-display italic text-white/80">{slide.tagline}</p>

            <div className="mt-6">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-white backdrop-blur-md">
                <MapPin size={14} className="text-accent-400 shrink-0" aria-hidden="true" />
                {cityCountry} · {displayRange}
              </span>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/call-for-papers#dates" className="btn-accent group shadow-glow-accent">
                Important Dates{' '}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/call-for-papers"
                className="btn border border-white/35 text-white hover:bg-white/15 backdrop-blur-sm"
              >
                Call for Papers
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* ── Slider controls ── */}
        <div className="mt-14 flex items-center gap-4 max-w-5xl">
          <button
            type="button"
            onClick={prev}
            className="p-2.5 rounded-full border border-white/20 bg-white/10 text-white
                       hover:bg-accent-500 hover:border-accent-500 hover:text-ink-950
                       transition-colors backdrop-blur-sm"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Progress dots */}
          <div className="flex gap-2">
            {HERO_SLIDES.map((s, i) => (
              <button
                key={s.id ?? s.image}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={cn(
                  'relative h-2 rounded-full overflow-hidden transition-all duration-500',
                  i === index ? 'w-14' : 'w-2 bg-white/40 hover:bg-white/70',
                )}
              >
                {i === index && (
                  <>
                    <span className="absolute inset-0 rounded-full bg-white/30" />
                    <span
                      className="absolute inset-y-0 left-0 rounded-full bg-accent-500"
                      style={{ width: `${progress * 100}%` }}
                    />
                  </>
                )}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={next}
            className="p-2.5 rounded-full border border-white/20 bg-white/10 text-white
                       hover:bg-accent-500 hover:border-accent-500 hover:text-ink-950
                       transition-colors backdrop-blur-sm"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
}
